import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { RefreshCw } from 'lucide-react';
import { iconBtnCls } from './styles';

interface PageHeaderProps {
  /** Page title (string or node). */
  title: ReactNode;
  /** Optional leading icon, rendered in the platform accent. */
  icon?: LucideIcon;
  /** Optional record count rendered after the title as "(1,234)". */
  count?: number | null;
  /** One quiet line under the title; omit for none. */
  subtitle?: ReactNode;
  /** Right-aligned actions (buttons, menus, toggles). */
  actions?: ReactNode;
  className?: string;
}

/**
 * Shared page header: one compact title row for every page in every tool —
 * icon + title + optional count/subtitle on the left, actions on the right.
 * Replaces the hand-rolled headers that had drifted across modules (h1 vs h2,
 * text-xl vs text-lg vs font-bold, multiple refresh-button variants).
 */
export default function PageHeader({ title, icon: Icon, count, subtitle, actions, className = '' }: PageHeaderProps) {
  return (
    <div className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-2 ${className}`}>
      <div className="min-w-0">
        <h1 className="flex items-center gap-2 text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
          {Icon && <Icon className="h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />}
          <span className="min-w-0 truncate">{title}</span>
          {typeof count === 'number' && (
            <span className="flex-shrink-0 text-sm font-normal text-gray-500 dark:text-gray-400">
              ({count.toLocaleString()})
            </span>
          )}
        </h1>
        {subtitle && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** The one standard refresh affordance for page headers. */
export function RefreshButton({
  onClick,
  loading = false,
  label = 'Refresh',
}: {
  onClick: () => void;
  loading?: boolean;
  label?: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={loading} title={label} aria-label={label} className={iconBtnCls}>
      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
    </button>
  );
}
